"use client";
import { CartesianGrid, Line, LineChart, XAxis, TooltipProps } from "recharts";
import { formatDate } from "date-fns";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "visuais";

type ChartLineDefaultProps = {
  chartData: {
    id: string;
    label: string;
    from: Date;
    to: Date;
    status: { name: string; color: string };
    owner: string;
    filial: string;
    observacao: string;
    dias: number;
    [key: string]: any;
  }[];
  chartConfig: ChartConfig;
  chartTitle: string;
  chartDesc?: string;
  strokeColor: string;
  xKey: string;
  yKey: string;
};

export function ChartLineDefault({
  chartData,
  chartConfig,
  chartTitle,
  chartDesc,
  strokeColor,
  xKey,
  yKey,
}: ChartLineDefaultProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{chartTitle}</CardTitle>
        <CardDescription>{chartDesc}</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-60 w-full">
          <LineChart data={chartData} margin={{ top: 20, left: 25, right: 40 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey={xKey}
              interval={0}
              allowDuplicatedCategory={false}
              tickLine={false}
              axisLine={false}
              tickMargin={0}
              tickFormatter={(value: string) =>
                value.includes(" - ") ? value.split(" - ")[1].trim() : value
              }
            />

            <ChartTooltip
              cursor={false}
              content={({ active, payload }: TooltipProps<any, any>) => {
                if (!active || !payload || !payload.length) return null;
                const entry = payload[0].payload;

                return (
                  <div
                    className="text-white text-xs rounded-md p-3 max-w-sm space-y-2 pointer-events-auto"
                    style={{ backgroundColor: entry.status?.color ?? "#000" }}
                  >
                    {/* Título + Filial */}
                    <div className="flex justify-between items-center">
                      <strong>{entry.label}</strong>
                      <span className="ml-3" title="Filial">
                        <strong>{entry.filial}</strong>
                      </span>
                    </div>

                    {/* Dias */}
                    <div>
                      <span className="font-medium">Dias:</span> {entry.dias}
                    </div>

                    {/* Datas */}
                    <div className="text-[12px]">
                      {entry.usedFallback
                        ? "Desconhecido"
                        : `${formatDate(entry.from, "dd/MM/yyyy")}`}{" "}
                      → {formatDate(entry.to, "dd/MM/yyyy")}
                    </div>

                    {/* Status + Usuário */}
                    <div className="flex justify-between items-center gap-2">
                      <div className="text-sm text-muted-foreground">
                        {entry.owner} - {entry.filial}
                      </div>
                      <strong>{entry.status?.name}</strong>
                    </div>
                  </div>
                );
              }}
            />
            <Line
              dataKey={yKey}
              type="natural"
              stroke={strokeColor}
              strokeWidth={2}
              dot={true}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
